/**
 * AddCategoryModal — «+ Добавить категорию» (matching-v2 §C).
 *
 * Минимальная форма: путь UA + путь RU (строкой, «/»-разделитель) + выбор
 * таргет-строк (unresolved / disputed). Применение: useResolveRow
 * (POST /datasets/{id}/matching/resolve-row) с кандидатом
 * {path_ua, path_ru, score: 1, source 'manual', status 'confirmed'} — BE
 * проставляет статус ручного подтверждения (status='manual').
 *
 * Путь принимается СТРОКОЙ — это честнее, чем «гадать» уровень дерева:
 * то, что человек вводит, то и уходит в карту.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';

import {
  usePipelineMatchingRows,
  useResolveRow,
  type MatchingCategoryCandidate,
} from '@/hooks/useMatching';

export interface AddCategoryModalProps {
  pipelineId: string | null;
  datasetId: string | null;
  onClose: () => void;
}

const SPLIT_RE = /\s*[/>→]\s*/; // «A / B», «A>B», «A → B»

function parsePath(raw: string): string[] {
  return raw
    .split(SPLIT_RE)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

export function AddCategoryModal({
  pipelineId,
  datasetId,
  onClose,
}: AddCategoryModalProps) {
  const { t } = useTranslation();
  const { data: rowsResp } = usePipelineMatchingRows(pipelineId);
  const resolve = useResolveRow();

  const [pathUa, setPathUa] = useState('');
  const [pathRu, setPathRu] = useState('');
  const [includeDisputed, setIncludeDisputed] = useState(false);
  const [applied, setApplied] = useState<{ rowId: string }[]>([]);
  const [errors, setErrors] = useState<string[]>([]);

  const targetRows = useMemo(() => {
    const rows = rowsResp?.rows ?? [];
    const out: { rowId: string; status: string }[] = [];
    for (const r of rows) {
      const md = r.mapped_data as
        | { rows?: Record<string, { status?: string }> }
        | null
        | undefined;
      const entry = md?.rows?.[r.id];
      if (!entry) continue;
      if (entry.status === 'unresolved') {
        out.push({ rowId: r.id, status: 'unresolved' });
      } else if (entry.status === 'disputed' && includeDisputed) {
        out.push({ rowId: r.id, status: 'disputed' });
      }
    }
    return out;
  }, [rowsResp, includeDisputed]);

  const readyToApply =
    datasetId != null &&
    parsePath(pathUa).length > 0 &&
    resolve.isPending === false;

  async function handleApply() {
    if (!datasetId || !readyToApply) return;
    const path_ua = parsePath(pathUa);
    const path_ru = parsePath(pathRu).length > 0 ? parsePath(pathRu) : path_ua;
    const failures: string[] = [];
    const appliedNow: { rowId: string }[] = [];
    for (const r of targetRows) {
      const candidate: MatchingCategoryCandidate = {
        path_ua,
        path_ru,
        category_id: `manual:${path_ua.join('/')}`,
        score: 1,
        status: 'confirmed',
      };
      try {
        await resolve.mutateAsync({
          dataset_id: datasetId,
          row_id: r.rowId,
          category: candidate,
        });
        appliedNow.push({ rowId: r.rowId });
      } catch {
        failures.push(r.rowId);
      }
    }
    setApplied(appliedNow);
    setErrors(failures);
    if (failures.length === 0 && appliedNow.length > 0) {
      setPathUa('');
      setPathRu('');
      setIncludeDisputed(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      data-testid="add-category-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="card w-full max-w-md space-y-3 p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-surface-300">
            {t('process.matching.addCategory.title', {
              defaultValue: 'Add category',
            })}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-surface-500 hover:text-surface-200"
            aria-label="close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-2">
          <div>
            <label className="text-xs text-surface-400" htmlFor="add-cat-ua">
              {t('process.matching.addCategory.pathUa', {
                defaultValue: 'Category path (UA)',
              })}
            </label>
            <input
              id="add-cat-ua"
              type="text"
              value={pathUa}
              onChange={(e) => setPathUa(e.target.value)}
              placeholder={t('process.matching.addCategory.pathExample', {
                defaultValue: 'Одяг / Куртки',
              })}
              className="input py-1 text-sm w-full mt-1"
            />
          </div>
          <div>
            <label className="text-xs text-surface-400" htmlFor="add-cat-ru">
              {t('process.matching.addCategory.pathRu', {
                defaultValue: 'Category path (RU, optional)',
              })}
            </label>
            <input
              id="add-cat-ru"
              type="text"
              value={pathRu}
              onChange={(e) => setPathRu(e.target.value)}
              placeholder={t('process.matching.addCategory.pathExampleRu', {
                defaultValue: 'Одежда / Куртки',
              })}
              className="input py-1 text-sm w-full mt-1"
            />
          </div>

          <label className="flex items-center gap-2 text-xs text-surface-300">
            <input
              type="checkbox"
              checked={includeDisputed}
              onChange={(e) => setIncludeDisputed(e.target.checked)}
              className="accent-primary-500"
            />
            {t('process.matching.addCategory.includeDisputed', {
              defaultValue:
                'Also apply to disputed rows (default: unresolved only)',
            })}
          </label>
        </div>

        <p className="text-xs text-surface-500">
          {t('process.matching.addCategory.targetHint', {
            defaultValue:
              'Rows marked unresolved will get this category as manual-confirmed.',
            n: targetRows.length,
          })}{' '}
          ({targetRows.length})
        </p>

        {applied.length > 0 && errors.length === 0 && (
          <p className="text-xs text-success">
            {t('process.matching.addCategory.applied', {
              defaultValue: 'Applied to {{n}} rows.',
              n: applied.length,
            })}
          </p>
        )}
        {errors.length > 0 && (
          <p className="text-xs text-danger">
            {t('process.matching.addCategory.errors', {
              defaultValue: '{{n}} row(s) failed — retry after refetch.',
              n: errors.length,
            })}
          </p>
        )}

        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary text-xs px-3 py-1.5"
          >
            {t('process.matching.addCategory.close', {
              defaultValue: 'Close',
            })}
          </button>
          <button
            type="button"
            onClick={handleApply}
            disabled={!readyToApply || targetRows.length === 0}
            className="btn-primary text-xs px-3 py-1.5"
          >
            {t('process.matching.addCategory.apply', {
              defaultValue: 'Apply to rows ({{n}})',
              n: targetRows.length,
            })}
          </button>
        </div>
      </div>
    </div>
  );
}